// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useGetPrepareClawbackHoldings } from '@hooks/other/useGetPrepareClawbackHoldings';
import { useClawbackReceiverDelegationsByParty } from '@hooks/queries/registry/clawback/useClawbackReceiverDelegations';
import { useInstrumentConfigurationsById } from '@hooks/queries/useGetAllInstrumentConfigurations';
import { useCallback, useState } from 'react';
import { InstrumentConfiguration } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Configuration/Instrument';
import { ContractId } from '@daml/types';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import useRegistrarService from '../../../hooks/queries/registry/onboarding/useRegistrarService';
import { getValidateAmountMessage } from '../../../utils/getValidateAmountMessage';
import { MaxLimitErrorMessage } from '../transfer/MaxLimitErrorMessage';
import { useClawback } from './useClawback';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  disableDialog?: boolean;
};

export const ClawbackDialog = ({ disableDialog, open, onClose }: DialogProps) => {
  const registrarService = useRegistrarService();
  const [clawbackFrom, setClawbackFrom] = useState<string>('');
  const [clawbackTo, setClawbackTo] = useState<string>('');
  const [tokenId, setTokenId] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [amountValidationMsg, setAmountValidationMsg] = useState('');
  const [showMaxLimitError, setShowMaxLimitError] = useState(false);

  const prepareClawbackHoldings = useGetPrepareClawbackHoldings();
  const clawback = useClawback();
  const delegations = useClawbackReceiverDelegationsByParty();
  const instruments = useInstrumentConfigurationsById();

  const onClawback = async () => {
    setShowMaxLimitError(false);

    const instrument = instruments[tokenId];

    if (!instrument) {
      throw Error(`Cannot find instrument configuration for token: ${tokenId}`);
    }

    const { holdingCids, transferOfferCids, allocationCids, exceedsMaxLimit } =
      prepareClawbackHoldings({
        instrumentId: instrument.payload.defaultIdentifier.id,
        registrar: instrument.payload.registrar,
        owner: clawbackFrom,
      });

    if (exceedsMaxLimit) {
      setShowMaxLimitError(true);
      return;
    }

    if (registrarService.data) {
      await clawback({
        registrarServiceCid: registrarService.data.contractId,
        instrumentConfigurationCid: instrument.contract
          .contractId as ContractId<InstrumentConfiguration>,
        inputHoldingCids: holdingCids,
        owner: clawbackFrom,
        receiverDelegation: clawbackTo
          ? { party: clawbackTo, cid: delegations[clawbackTo].contractId }
          : undefined,
        clawbackAmount: amount,
        transferOfferCids,
        allocationCids,
      });
    }

    onClose();
  };

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const getTransferValidationMsg = getValidateAmountMessage('Clawback');

  const disableSubmit =
    !registrarService.data ||
    !amount ||
    !!amountValidationMsg.length ||
    (clawbackTo && !delegations[clawbackTo]) ||
    !tokenId ||
    !instruments[tokenId] ||
    !clawbackFrom;

  return (
    <FormDialog
      disableDialog={disableDialog}
      open={open}
      title="Clawback"
      onCancel={handleClose}
      onSubmit={onClawback}
      submitName="Clawback"
      isSubmitDisabled={disableSubmit}
    >
      <TextInput
        enableTopLabel
        label="Instrument ID"
        value={tokenId}
        setValue={setTokenId}
        required
      />
      <TextInput
        enableTopLabel
        label="Clawback From"
        value={clawbackFrom}
        setValue={setClawbackFrom}
        required
      />
      <TextInput
        enableTopLabel
        label="Clawback To (Blank for Registrar)"
        value={clawbackTo}
        setValue={setClawbackTo}
      />
      <TextInput
        enableTopLabel
        label="Amount"
        getValidationMessage={getTransferValidationMsg}
        setValidationMessage={setAmountValidationMsg}
        validationMessage={amountValidationMsg}
        value={amount}
        setValue={setAmount}
        required
        type="number"
        inputProps={{ step: 'any' }}
      />

      {showMaxLimitError && <MaxLimitErrorMessage />}
    </FormDialog>
  );
};
